/* eslint-disable react/no-danger */
// @noflow
import { FlavourConstituent, Meal, ProductBenefit } from '@/gql/graphql'
import React, { useCallback, useState } from 'react'
import Truncate from 'react-truncate-html'

import { Accordion } from '@/components/elements/atoms/Accordion'
import { cloudinaryPath } from '@/components/elements/atoms/CloudinaryImage/CloudinaryImage'
import FlatButton from '@/components/elements/atoms/FlatButton/FlatButton'
import { SectionWrapper } from '@/components/elements/atoms/SectionWrapper'
import Text from '@/components/elements/atoms/Text'

import STYLES from './RecipeDetail.module.sass'

import ModalLayout from '../../layout'

type Props = {
  data: {
    product: Meal
  }
}

const Benefits = ({
  benefits
}: {
  benefits: ProductBenefit[]
}): JSX.Element => (
  <div className={STYLES.benefits}>
    {benefits.map((benefit) => (
      <div key={benefit.id}>
        {benefit?.image?.src && (
          <img
            src={cloudinaryPath({
              path: benefit?.image?.src,
              transformations: { format: 'svg', width: 32 }
            })}
            alt={benefit.name}
          />
        )}
        <Text text={benefit.name} translate={false} margin={false} />
      </div>
    ))}
  </div>
)

const TastingNotes = ({
  tastingNotes
}: {
  tastingNotes: string
}): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleReadMoreClick = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const truncatable = tastingNotes.length > 250

  return (
    <SectionWrapper
      className={STYLES.markdownWrapper}
      bgColour="brandYellow200"
      padding={24}
      borderRadius={16}
      margin={{ top: 16 }}
    >
      <Text
        variant="display18"
        namespace="account"
        text="edit_recipes.fresh_meals_page.tasting_notes"
        margin={false}
      />

      {truncatable && !isExpanded ? (
        <Truncate
          lines={2}
          dangerouslySetInnerHTML={{ __html: tastingNotes }}
        />
      ) : (
        <div dangerouslySetInnerHTML={{ __html: tastingNotes }} />
      )}

      {truncatable && (
        <div>
          <FlatButton
            className={STYLES.readMoreButton}
            variant="link"
            text={{
              namespace: 'account',
              text: isExpanded
                ? 'edit_recipes.fresh_meals_page.read_less'
                : 'edit_recipes.fresh_meals_page.read_more',
              underline: true
            }}
            onClick={handleReadMoreClick}
            identifier="recipe-detail-modal.toggle-truncate"
          />
        </div>
      )}
    </SectionWrapper>
  )
}

const Accordions = ({
  composition,
  flavourConstituents,
  additives
}: {
  composition: string
  additives: string
  flavourConstituents: FlavourConstituent[]
}) => {
  return (
    <div>
      <Accordion
        title={{
          text: 'edit_recipes.fresh_meals_page.composition',
          namespace: 'account'
        }}
      >
        <p
          className={STYLES.markdownWrapper}
          dangerouslySetInnerHTML={{ __html: composition }}
        />
      </Accordion>

      <Accordion
        title={{
          text: 'edit_recipes.fresh_meals_page.nutritional_information',
          namespace: 'account'
        }}
      >
        <Text
          namespace="account"
          text="edit_recipes.fresh_meals_page.analtical_constituants"
          variant="display16"
          bold
        />

        {flavourConstituents.map((flavourConstituent) => (
          <div key={flavourConstituent.constituent.name}>
            <Text
              margin={false}
              text={`${
                flavourConstituent.constituent.name
              } ${flavourConstituent.percentage.toFixed(1)}%`}
              translate={false}
            />
          </div>
        ))}

        <Text
          namespace="account"
          text="edit_recipes.fresh_meals_page.nutritional_additives"
          variant="display16"
          bold
        />

        <p
          className={STYLES.markdownWrapper}
          dangerouslySetInnerHTML={{ __html: additives }}
        />
      </Accordion>
    </div>
  )
}

const RecipeDetail = ({ data }: Props): JSX.Element => {
  const { flavour } = data.product

  const images = flavour.images.map((image) => ({
    src: cloudinaryPath({ path: image.src, transformations: { height: 200 } }),
    alt: flavour.name,
    height: 200
  }))

  return (
    <ModalLayout
      title={{ text: flavour.name, translate: false }}
      images={images}
    >
      <Benefits benefits={flavour.productBenefits} />

      <TastingNotes tastingNotes={flavour.longDescription} />

      <Accordions
        composition={flavour.composition}
        additives={flavour.additives}
        flavourConstituents={flavour.flavourConstituents}
      />
    </ModalLayout>
  )
}

export { RecipeDetail }
